import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Edit3, Trash2, Copy, Eye, EyeOff, Calendar, Percent, DollarSign, Users, Tag, Search, Filter, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';

interface DiscountCode {
  id: string;
  business_id: string;
  code: string;
  description?: string;
  type: 'percentage' | 'fixed';
  value: number;
  min_order_amount?: number;
  max_uses?: number;
  used_count: number;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function DiscountCodes() {
  const { selectedBusiness } = useBusiness();
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'expired'>('all');

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage' as 'percentage' | 'fixed',
    value: 0,
    min_order_amount: '',
    max_uses: '',
    expires_at: '',
    is_active: true
  });

  // Load discount codes
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadDiscountCodes();
    }
  }, [selectedBusiness?.id]);

  const loadDiscountCodes = async () => {
    if (!selectedBusiness?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDiscountCodes(data || []);
    } catch (error) {
      console.error('Error loading discount codes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCode = async () => {
    if (!selectedBusiness?.id || !formData.code.trim()) return;

    try {
      const codeData = {
        business_id: selectedBusiness.id,
        code: formData.code.toUpperCase().trim(),
        description: formData.description.trim() || null,
        type: formData.type,
        value: formData.value,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active,
        used_count: 0
      };

      const { error } = await supabase
        .from('discount_codes')
        .insert([codeData]);

      if (error) throw error;

      await loadDiscountCodes();
      resetForm();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating discount code:', error);
      alert('Failed to create discount code. Please try again.');
    }
  };

  const handleUpdateCode = async () => {
    if (!editingCode || !formData.code.trim()) return;

    try {
      const updateData = {
        code: formData.code.toUpperCase().trim(),
        description: formData.description.trim() || null,
        type: formData.type,
        value: formData.value,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('discount_codes')
        .update(updateData)
        .eq('id', editingCode.id);

      if (error) throw error;

      await loadDiscountCodes();
      resetForm();
      setEditingCode(null);
    } catch (error) {
      console.error('Error updating discount code:', error);
      alert('Failed to update discount code. Please try again.');
    }
  };

  const handleDeleteCode = async (codeId: string) => {
    if (!confirm('Are you sure you want to delete this discount code?')) return;

    try {
      const { error } = await supabase
        .from('discount_codes')
        .delete()
        .eq('id', codeId);

      if (error) throw error;
      await loadDiscountCodes();
    } catch (error) {
      console.error('Error deleting discount code:', error);
      alert('Failed to delete discount code. Please try again.');
    }
  };

  const handleToggleStatus = async (code: DiscountCode) => {
    try {
      const { error } = await supabase
        .from('discount_codes')
        .update({ 
          is_active: !code.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', code.id);

      if (error) throw error;
      await loadDiscountCodes();
    } catch (error) {
      console.error('Error toggling code status:', error);
      alert('Failed to update code status. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      type: 'percentage',
      value: 0,
      min_order_amount: '',
      max_uses: '',
      expires_at: '',
      is_active: true
    });
  };

  const openEditModal = (code: DiscountCode) => {
    setFormData({
      code: code.code,
      description: code.description || '',
      type: code.type,
      value: code.value,
      min_order_amount: code.min_order_amount?.toString() || '',
      max_uses: code.max_uses?.toString() || '',
      expires_at: code.expires_at ? code.expires_at.split('T')[0] : '',
      is_active: code.is_active
    });
    setEditingCode(code);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const filteredCodes = discountCodes.filter(code => {
    const matchesSearch = code.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         code.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && code.is_active && !isExpired(code.expires_at)) ||
                         (filterStatus === 'inactive' && !code.is_active) ||
                         (filterStatus === 'expired' && isExpired(code.expires_at));

    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Loading discount codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-transparent"
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
      }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Discount Codes</h2>
              <p className="text-[15px] text-gray-600 mt-0.5 sm:mt-1" style={{ letterSpacing: '-0.01em' }}>Create and manage promotional discount codes</p>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] font-semibold transition-all duration-150 shadow-sm touch-manipulation"
              style={{ 
                minHeight: '44px',
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: '-0.01em',
                fontSize: '15px'
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Code
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search discount codes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200/60 rounded-[10px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-500 shadow-sm text-[15px]"
              style={{ 
                minHeight: '44px',
                letterSpacing: '-0.01em'
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-4 py-2.5 border border-gray-200/60 rounded-[10px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 shadow-sm text-[15px]"
              style={{ 
                minHeight: '44px',
                letterSpacing: '-0.01em'
              }}
            >
              <option value="all">All Codes</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        {/* Discount Codes List */}
        <div className="bg-white rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 overflow-hidden">
          {filteredCodes.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 rounded-[10px] bg-gray-100 flex items-center justify-center shadow-sm border border-gray-200/60">
                <Tag className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>No discount codes found</h3>
              <p className="text-[15px] text-gray-600 mb-4" style={{ letterSpacing: '-0.01em' }}>
                {searchQuery || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Create your first discount code to start offering promotions to your customers.'
                }
              </p>
              {!searchQuery && filterStatus === 'all' && (
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] font-semibold transition-all duration-150 shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    fontSize: '15px'
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Code
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '0.05em' }}>Code</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '0.05em' }}>Discount</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '0.05em' }}>Usage</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '0.05em' }}>Status</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '0.05em' }}>Expires</th>
                    <th className="px-4 sm:px-6 py-3 text-right text-[11px] font-medium text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '0.05em' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200/60">
                  {filteredCodes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-50 active:bg-gray-100 transition-colors">
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center">
                          <div className="font-mono font-semibold text-[15px] text-gray-900 bg-gray-50 px-2 py-1 rounded-[6px] border border-gray-200/60" style={{ letterSpacing: '-0.01em' }}>
                            {code.code}
                          </div>
                          <button
                            onClick={() => copyToClipboard(code.code)}
                            className="ml-2 p-2 active:bg-gray-100 rounded-[10px] transition-all duration-150 touch-manipulation"
                            title="Copy code"
                            style={{ 
                              minHeight: '36px',
                              minWidth: '36px',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            <Copy className="h-4 w-4 text-gray-500" />
                          </button>
                        </div>
                        {code.description && (
                          <div className="text-[13px] text-gray-600 mt-1" style={{ letterSpacing: '-0.01em' }}>{code.description}</div>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {code.type === 'percentage' ? (
                            <Percent className="h-4 w-4 text-green-600 mr-1.5" />
                          ) : (
                            <DollarSign className="h-4 w-4 text-green-600 mr-1.5" />
                          )}
                          <span className="font-semibold text-[15px] text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                            {code.type === 'percentage' ? `${code.value}%` : `$${code.value}`}
                          </span>
                        </div>
                        {code.min_order_amount && (
                          <div className="text-[13px] text-gray-500 mt-0.5" style={{ letterSpacing: '-0.01em' }}>Min: ${code.min_order_amount}</div>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-blue-600 mr-1.5" />
                          <span className="text-[15px] font-medium text-gray-900" style={{ letterSpacing: '-0.01em' }}>{code.used_count}</span>
                          {code.max_uses && <span className="text-[13px] text-gray-500 ml-1" style={{ letterSpacing: '-0.01em' }}>/ {code.max_uses}</span>}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-[6px] ${
                          !code.is_active 
                            ? 'bg-gray-100 text-gray-800'
                            : isExpired(code.expires_at)
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`} style={{ letterSpacing: '-0.01em' }}>
                          {!code.is_active ? 'Inactive' : isExpired(code.expires_at) ? 'Expired' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-[13px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        {code.expires_at ? (
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1.5" />
                            {new Date(code.expires_at).toLocaleDateString()}
                          </div>
                        ) : (
                          'Never'
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleStatus(code)}
                            className="p-2 active:bg-gray-100 rounded-[10px] transition-all duration-150 touch-manipulation"
                            title={code.is_active ? 'Deactivate' : 'Activate'}
                            style={{ 
                              minHeight: '36px',
                              minWidth: '36px',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            {code.is_active ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                          </button>
                          <button
                            onClick={() => openEditModal(code)}
                            className="p-2 active:bg-blue-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                            title="Edit"
                            style={{ 
                              minHeight: '36px',
                              minWidth: '36px',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            <Edit3 className="h-4 w-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteCode(code.id)}
                            className="p-2 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                            title="Delete"
                            style={{ 
                              minHeight: '36px',
                              minWidth: '36px',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || editingCode) && createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{
            zIndex: 100010,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setIsCreateModalOpen(false);
              setEditingCode(null);
              resetForm();
            }
          }}
        >
          <div 
            className="bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 fixed inset-0 w-full md:inset-y-0 md:right-0 md:left-auto md:w-[500px]"
            style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100050,
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                {editingCode ? 'Edit Discount Code' : 'Create Discount Code'}
              </h3>
              <button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setEditingCode(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div 
              className="flex-1 overflow-y-auto bg-white"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="p-6 space-y-6">
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="SAVE20"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 font-mono"
                    style={{ fontSize: '16px' }}
                    required
                  />
                </div>

                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="20% off all services"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Type *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'percentage' | 'fixed' })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Value *
                      </label>
                      <input
                        type="number"
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                        placeholder={formData.type === 'percentage' ? '20' : '10.00'}
                        min="0"
                        step={formData.type === 'percentage' ? '1' : '0.01'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Min Order Amount
                      </label>
                      <input
                        type="number"
                        value={formData.min_order_amount}
                        onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                        placeholder="50.00"
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Max Uses
                      </label>
                      <input
                        type="number"
                        value={formData.max_uses}
                        onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                        placeholder="100"
                        min="1"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expires At
                  </label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-3 block text-sm font-semibold text-gray-900">
                      Active
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setEditingCode(null);
                  resetForm();
                }}
                className="px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={editingCode ? handleUpdateCode : handleCreateCode}
                className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {editingCode ? 'Update' : 'Create'} Code
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
